/**
 * Piezas visuales de la consola SuperAdmin con la identidad de Finance360 (estilos/finance.css,
 * bajo `.f360`): tarjetas blancas con borde, índigo como acento, botones delineados.
 */
import React, { useState } from 'react';
import clsx from 'clsx';
import { AlertOctagon, AlertTriangle, Info, X, Loader2 } from 'lucide-react';
import useEscClose from '../../hooks/useEscClose';

// ───────── formatos (siempre hora de Costa Rica) ─────────
const TZ = 'America/Costa_Rica';
export const fFecha = (d) => d ? new Date(d).toLocaleDateString('es-CR', { timeZone: TZ, day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
export const fFechaHora = (d) => d ? new Date(d).toLocaleString('es-CR', { timeZone: TZ, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
export const fNum = (n) => new Intl.NumberFormat('es-CR').format(Number(n) || 0);
export const fColones = (n, compacto = false) => {
  const v = Number(n) || 0;
  if (compacto && Math.abs(v) >= 1e6) return `₡${(v / 1e6).toLocaleString('es-CR', { timeZone: 'America/Costa_Rica', maximumFractionDigits: 1 })} M`;
  return `₡${v.toLocaleString('es-CR', { timeZone: 'America/Costa_Rica', maximumFractionDigits: 0 })}`;
};
export const fMonto = (n, moneda = 'CRC') => `${moneda === 'USD' ? '$' : '₡'}${(Number(n) || 0).toLocaleString('es-CR', { timeZone: 'America/Costa_Rica', minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const fBytes = (b) => {
  const n = Number(b) || 0;
  if (!n) return '0 MB';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), u.length - 1);
  return `${(n / Math.pow(1024, i)).toFixed(i > 1 ? 1 : 0)} ${u[i]}`;
};
export const hace = (d) => {
  if (!d) return 'nunca';
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return 'hace un momento';
  if (s < 3600) return `hace ${Math.floor(s / 60)} min`;
  if (s < 86400) return `hace ${Math.floor(s / 3600)} h`;
  const dias = Math.floor(s / 86400);
  return dias === 1 ? 'ayer' : `hace ${dias} días`;
};

// ───────── contenedores ─────────
export function Encabezado({ titulo, subtitulo, children }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-6">
      <div>
        <h1 className="text-[27px] font-extrabold leading-tight text-gray-900">{titulo}</h1>
        {subtitulo && <p className="text-sm text-gray-500 mt-1">{subtitulo}</p>}
      </div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

export function Panel({ titulo, icono: Icono, accion, children, className, sinPadding }) {
  return (
    <section className={clsx('bg-white rounded-xl border border-gray-200', className)}>
      {titulo && (
        <div className="flex items-center justify-between gap-2 px-5 py-3.5 border-b border-gray-100">
          <h2 className="font-bold text-gray-900 text-[15px] flex items-center gap-2">
            {Icono && <Icono className="h-4 w-4 text-[#4652B8]" />}{titulo}
          </h2>
          {accion}
        </div>
      )}
      <div className={sinPadding ? '' : 'p-5'}>{children}</div>
    </section>
  );
}

export function Kpi({ icono: Icono, color = 'blue', titulo, valor, detalle }) {
  const colores = {
    blue: 'bg-blue-50 text-blue-600', green: 'bg-emerald-50 text-emerald-600', purple: 'bg-violet-50 text-violet-600',
    amber: 'bg-amber-50 text-amber-600', slate: 'bg-slate-100 text-slate-600', red: 'bg-red-50 text-red-600'
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-start gap-3">
      <div className={clsx('p-2.5 rounded-lg shrink-0', colores[color])}><Icono className="h-5 w-5" /></div>
      <div className="min-w-0">
        <p className="f360-ojo">{titulo}</p>
        <p className="text-[28px] font-extrabold tracking-tight text-gray-900 leading-tight mt-1 truncate" style={{ fontFamily: 'var(--display)' }}>{valor}</p>
        {detalle && <p className="text-xs text-gray-500 mt-0.5 truncate">{detalle}</p>}
      </div>
    </div>
  );
}

// ───────── estados ─────────
const ESTADO_CUENTA = {
  active: { label: 'Activa', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  suspended: { label: 'Suspendida', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  cancelled: { label: 'Cancelada', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
  trial: { label: 'Prueba', cls: 'bg-blue-50 text-blue-700 border-blue-200' }
};
export function EstadoCuenta({ estado }) {
  const e = ESTADO_CUENTA[estado] || { label: estado, cls: 'bg-gray-100 text-gray-600 border-gray-200' };
  return <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border', e.cls)}>{e.label}</span>;
}

export const SEVERIDAD = {
  critica: { label: 'Crítico', icono: AlertOctagon, cls: 'text-red-700 bg-red-50 border-red-200', punto: 'bg-red-500' },
  aviso: { label: 'Atención', icono: AlertTriangle, cls: 'text-amber-800 bg-amber-50 border-amber-200', punto: 'bg-amber-500' },
  info: { label: 'Info', icono: Info, cls: 'text-blue-700 bg-blue-50 border-blue-200', punto: 'bg-blue-500' }
};
export function Severidad({ nivel }) {
  const s = SEVERIDAD[nivel] || SEVERIDAD.info;
  const I = s.icono;
  return <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border', s.cls)}><I className="h-3 w-3" />{s.label}</span>;
}

/** Salud de una cuenta según sus alertas: un punto de color + texto. */
export function Salud({ alertas, error }) {
  if (error) return <span className="inline-flex items-center gap-1.5 text-xs font-medium text-red-700"><span className="h-2 w-2 rounded-full bg-red-500" />Sin acceso a la base</span>;
  const { criticas = 0, avisos = 0 } = alertas || {};
  if (criticas) return <span className="inline-flex items-center gap-1.5 text-xs font-medium text-red-700"><span className="h-2 w-2 rounded-full bg-red-500" />{criticas} crítico{criticas > 1 ? 's' : ''}</span>;
  if (avisos) return <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700"><span className="h-2 w-2 rounded-full bg-amber-500" />{avisos} por revisar</span>;
  return <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700"><span className="h-2 w-2 rounded-full bg-emerald-500" />Al día</span>;
}

// ───────── botones ─────────
export function Boton({ variante = 'neutro', icono: Icono, cargando, children, className, ...props }) {
  const v = {
    neutro: 'border-gray-300 text-gray-700 hover:bg-gray-50',
    // Outline siempre (como HelpDesk360): el primario es índigo delineado, no relleno
    primario: 'border-[#4652B8] bg-white text-[#3B45A6] hover:bg-[#ECEEFA] border-[1.5px]',
    azul: 'border-[#BCC3EE] text-[#3B45A6] hover:bg-[#ECEEFA]',
    verde: 'border-emerald-500 text-emerald-700 hover:bg-emerald-50',
    ambar: 'border-amber-400 text-amber-700 hover:bg-amber-50',
    rojo: 'border-red-400 text-red-600 hover:bg-red-50'
  };
  return (
    <button {...props} disabled={props.disabled || cargando}
      className={clsx('inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-[10px] border text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed', v[variante], className)}>
      {cargando ? <Loader2 className="h-4 w-4 animate-spin" /> : Icono && <Icono className="h-4 w-4" />}
      {children}
    </button>
  );
}

export function Cargando({ texto = 'Cargando…' }) {
  return <div className="flex items-center justify-center gap-2 py-16 text-gray-500 text-sm"><Loader2 className="h-5 w-5 animate-spin text-blue-600" />{texto}</div>;
}

export function Vacio({ icono: Icono, titulo, texto }) {
  return (
    <div className="text-center py-12 px-4">
      {Icono && <Icono className="h-10 w-10 text-gray-300 mx-auto mb-3" />}
      <p className="text-gray-700 font-medium">{titulo}</p>
      {texto && <p className="text-sm text-gray-500 mt-1">{texto}</p>}
    </div>
  );
}

/** Ventana ancha (dos columnas cabe cómodo), cierra con Esc o clic afuera. */
export function Modal({ titulo, icono: Icono, onClose, children, pie, ancho = 'max-w-3xl', tono }) {
  useEscClose(onClose);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className={clsx('relative w-full bg-white rounded-xl shadow-2xl max-h-[90vh] flex flex-col', ancho)}>
        <div className={clsx('flex items-center justify-between px-5 py-4 border-b rounded-t-xl', tono === 'peligro' ? 'bg-red-50' : '')}>
          <h3 className={clsx('text-lg font-semibold flex items-center gap-2', tono === 'peligro' ? 'text-red-800' : 'text-gray-900')}>
            {Icono && <Icono className="h-5 w-5" />}{titulo}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto">{children}</div>
        {pie && <div className="flex justify-end gap-2 px-5 py-3 border-t bg-gray-50 rounded-b-xl">{pie}</div>}
      </div>
    </div>
  );
}

export const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
export function Campo({ label, children, ayuda, className }) {
  return (
    <label className={clsx('block text-xs font-medium text-gray-700', className)}>
      {label}
      <div className="mt-1">{children}</div>
      {ayuda && <span className="block font-normal text-gray-500 mt-1">{ayuda}</span>}
    </label>
  );
}

/** Aviso en línea (éxito/error), para no depender de alert(). */
export function Aviso({ aviso, onClose }) {
  if (!aviso) return null;
  return (
    <div className={clsx('mb-4 flex items-start justify-between gap-3 px-4 py-3 rounded-lg border text-sm',
      aviso.tipo === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800')}>
      <span className="whitespace-pre-line">{aviso.texto}</span>
      {onClose && <button onClick={onClose} className="opacity-60 hover:opacity-100"><X className="h-4 w-4" /></button>}
    </div>
  );
}

/**
 * Barras por día (una sola serie: sin leyenda, el título la nombra). Barras finas con
 * esquinas redondeadas arriba, grilla tenue, tooltip al pasar el mouse.
 */
export function BarrasDiarias({ datos, alto = 180, etiqueta = 'comprobantes' }) {
  const [hover, setHover] = useState(null);
  const max = Math.max(1, ...datos.map(d => d.valor));
  const paso = max <= 5 ? 1 : Math.ceil(max / 4);
  const tope = Math.ceil(max / paso) * paso;
  const W = 100 / datos.length;
  const fechaCorta = (f) => { const [, m, d] = f.split('-'); return `${d}/${m}`; };
  return (
    <div className="relative">
      <div className="flex">
        <div className="flex flex-col justify-between text-[10px] text-gray-400 pr-2 text-right" style={{ height: alto }}>
          {Array.from({ length: tope / paso + 1 }, (_, i) => tope - i * paso).map(v => <span key={v} className="leading-none">{v}</span>)}
        </div>
        <div className="relative flex-1" style={{ height: alto }}>
          {Array.from({ length: tope / paso + 1 }, (_, i) => (
            <div key={i} className="absolute left-0 right-0 border-t border-gray-100" style={{ top: `${(i * paso / tope) * 100}%` }} />
          ))}
          <div className="absolute inset-0 flex items-end">
            {datos.map((d, i) => (
              <div key={d.fecha} className="h-full flex items-end justify-center" style={{ width: `${W}%` }}
                onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
                <div className={clsx('w-[60%] max-w-[18px] rounded-t transition-colors', hover === i ? 'bg-[#2C3380]' : 'bg-[#4652B8]')}
                  style={{ height: `${(d.valor / tope) * 100}%`, minHeight: d.valor ? 2 : 0 }} />
              </div>
            ))}
          </div>
          {hover !== null && (
            <div className="absolute -top-2 pointer-events-none -translate-x-1/2 -translate-y-full bg-gray-900 text-white text-xs rounded-md px-2 py-1 whitespace-nowrap shadow"
              style={{ left: `${(hover + 0.5) * W}%` }}>
              {fechaCorta(datos[hover].fecha)} · <span className="font-semibold">{fNum(datos[hover].valor)}</span> {etiqueta}
            </div>
          )}
        </div>
      </div>
      <div className="flex ml-6 mt-1.5 text-[10px] text-gray-400">
        {datos.map((d, i) => (
          <span key={d.fecha} className="text-center" style={{ width: `${W}%` }}>{i % 5 === 0 || i === datos.length - 1 ? fechaCorta(d.fecha) : ''}</span>
        ))}
      </div>
    </div>
  );
}
